import React from "react";
import {
  useWSModalCustom,
  WSElement,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSInfoBox,
  WSList,
  WSModal,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import {
  ContextItem,
  RequirementAdditionalData,
  RequirementStatusViewProps
} from "../../types";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { usePayeeRowsSummaryQuery } from "../../../../query/search/payee/queries/usePayeeRowsSummaryQuery";
import { useUpdatePayeeEngagementRequirement } from "../../../../query/payeeEngagements/mutations/useUpdatePayeeEngagementRequirement";

interface RevertRequirementModalProps {
  onClose: () => void;
  requirement: IRequirementResponse;
  context: ContextItem;
  additionalData: RequirementAdditionalData;
  onSuccess: () => void;
  currentStatus: RequirementStatusViewProps;
}

const RevertRequirementModal: React.FC<RevertRequirementModalProps> = ({
  onClose,
  requirement,
  context,
  onSuccess,
  currentStatus
}) => {
  const [changeRequirementStatus, changeRequirementStatusMeta] =
    useUpdatePayeeEngagementRequirement();

  const queryPayeeGeneralSummary = usePayeeRowsSummaryQuery({
    filter: {
      "engagements.requirements.requirementId": requirement.requirementId
    }
  });

  const onSubmit = () => {
    changeRequirementStatus(
      {
        payeeId: requirement.payeeId,
        requirementId: requirement.requirementId,
        engagementId: requirement.payerPayeeEngagementIds[0]!,
        request: { status: null, reason: null }
      },
      {
        onSuccess() {
          onSuccess();
          onClose();
        }
      }
    );
  };

  const contractorsLength = queryPayeeGeneralSummary.data?.listSize || "-";

  return (
    <WSForm onSubmit={onSubmit} defaultValues={{}}>
      <WSModal
        title="Revert to synced status"
        onClose={onClose}
        actions={{
          alignment: "fill",
          buttons: [
            {
              kind: "Secondary",
              label: "Cancel",
              onClick: onClose,
              type: "button"
            },
            {
              kind: "Primary",
              label: "Yes, revert to synced status",
              type: "submit",
              loading: changeRequirementStatusMeta.isLoading
            }
          ]
        }}
      >
        <WSList gap="XL">
          <WSFlexBox gap="M" wrap="nowrap">
            <WSElement>
              <WSText.ParagraphSm color="gray600" mb="S">
                Current status:
              </WSText.ParagraphSm>
              <WSPill {...currentStatus.pill}></WSPill>
              <WSText.ParagraphXs mt="XS" ml="S" color="gray400">
                {currentStatus.secondaryText}
              </WSText.ParagraphXs>
            </WSElement>
            <WSIcon
              style={{
                marginTop: "auto",
                marginBottom: "auto"
              }}
              name="arrow-right"
            />

            <WSElement>
              <WSText.ParagraphSm color="gray600" mb="S">
                Overwritten status preview:
              </WSText.ParagraphSm>
              <WSPill
                text="Awaiting contractor"
                theme="neutral"
                icon="time"
              ></WSPill>
              <WSText.ParagraphXs mt="XS" ml="S" color="gray400">
                Awaiting contractor completion
              </WSText.ParagraphXs>
            </WSElement>
          </WSFlexBox>

          <WSInfoBox>
            <WSText.ParagraphSm color="gray600">
              By proceeding, the overwritten status will be removed and revert
              to the synced status from the requirement service. This
              requirement is assigned to {contractorsLength} of the contractor’s
              engagements and may affect their eligibility to receive payments
              from you. Would you like to proceed?
            </WSText.ParagraphSm>
          </WSInfoBox>
          <WSErrorMessage
            mt="XL"
            contextKey="Other"
            error={changeRequirementStatusMeta.isError}
          />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useRevertRequirementModal = () =>
  useWSModalCustom(RevertRequirementModal);
